import { generatePath, matchPath } from 'react-router';
import { all, call, fork, put, select, take, takeEvery, delay, takeLatest } from 'redux-saga/effects';

import { FETCH_DEALER_FAILURE, FETCH_DEALER_SUCCESS } from 'constants/ActionTypes';
import { SEARCH } from 'constants/FilterConstants';
import { PATH } from 'constants/RoutesConstants';
import { putAndWait } from 'modules/app/sagas';
import { scopeToPage } from 'modules/app/sagas/utils';
import { fetchCampaignFiles, setCampaignFilesFilter } from 'modules/campaignFiles/actions/core';
import { FETCH_CAMPAIGN_FILES_FAILURE, FETCH_CAMPAIGN_FILES_SUCCESS } from 'modules/campaignFiles/actionTypes';
import { getCampaignFilesParams } from 'modules/campaignFiles/selectors';
import * as core from 'modules/campaigns/actions/core';
import * as actions from 'modules/campaigns/actions/details';
import * as types from 'modules/campaigns/actionTypes';
import { CAMPAIGN_STATUS_ENUM, DETAILS_SOURCE } from 'modules/campaigns/constants';
import { fetchContactList } from 'modules/contactLists/actions/core';
import { FETCH_CONTACT_LIST_FAILURE, FETCH_CONTACT_LIST_SUCCESS } from 'modules/contactLists/actionTypes';
import { selectDealer } from 'modules/dealers/actions';
import { navigate } from 'modules/router/navigate';
import { getUserGroups } from 'modules/userGroups/actions';
import { getUsers } from 'modules/users/actions';
import { FETCH_USERS_FAILURE, FETCH_USERS_SUCCESS } from 'modules/users/actionTypes';
import { getSelectedDealerId } from 'selectors/dealerSelectors';
import { getCurrentLocation, getLocationSearch } from 'selectors/router';

import { uniq } from 'utils/ObjectUtils';

import { afterCreateCampaign, duplicateCampaignMatcher } from './common';
import campaignSocket, {
  WS_CAMPAIGN_BULK_FOLLOW_COMPLETED,
  WS_CAMPAIGN_BULK_INTERVENTIONS_CREATED_COMPLETED,
} from './sockets';
import { getSelectedCampaign, getSelectedCampaignId, isSelectedCampaignTriggered } from '../selectors/details';

const shouldGoBackToDashboard = ({ type, meta }) =>
  type === types.DELETE_CAMPAIGN_SUCCESS ||
  (type === types.PATCH_CAMPAIGN_STATUS_SUCCESS && meta.status === CAMPAIGN_STATUS_ENUM.CLOSED);

function* checkSelectedDealer({ dealerId }) {
  const selected = yield select(getSelectedDealerId);
  if (dealerId !== selected) {
    yield put(selectDealer(dealerId));
    yield take([FETCH_DEALER_SUCCESS, FETCH_DEALER_FAILURE]);
  }
}

function* fetchUsers(campaign) {
  const userField = ['assignedUser', 'contactPerson'];
  const ids = userField.reduce((acc, field) => {
    const userId = campaign[field]?.id;
    if (userId) {
      return uniq([...acc, userId]);
    }
    return acc;
  }, []);

  if (ids.length > 0) {
    yield put(getUsers({ ids, size: ids.length }));
    yield take([FETCH_USERS_SUCCESS, FETCH_USERS_FAILURE]);
  }
}

function* fetchContactListDetails(campaign) {
  const contactListId = campaign.contactList?.id;
  if (contactListId) {
    yield put(fetchContactList(contactListId));
    yield take([FETCH_CONTACT_LIST_SUCCESS, FETCH_CONTACT_LIST_FAILURE]);
  }
}

function* initDetailsPage() {
  const location = yield select(getCurrentLocation);
  const searchParams = yield select(getLocationSearch);
  const match = matchPath(PATH.CAMPAIGNS_DETAILS.path, location);

  const { campaignId } = match.params;
  const search = new URLSearchParams(searchParams).get(SEARCH);

  const { error, payload: campaign } = yield putAndWait(
    core.fetchCampaign(match.params.campaignId, { source: DETAILS_SOURCE })
  );

  if (!error) {
    yield all([
      call(checkSelectedDealer, campaign),
      putAndWait(getUserGroups(campaign.dealerId)),
      call(fetchUsers, campaign),
      call(fetchContactListDetails, campaign),
    ]);

    const isTriggered = yield select(isSelectedCampaignTriggered);
    if (isTriggered) {
      if (search) {
        yield put(setCampaignFilesFilter(SEARCH, search));
        yield take([FETCH_CAMPAIGN_FILES_SUCCESS, FETCH_CAMPAIGN_FILES_FAILURE]);
      } else {
        const params = yield select(getCampaignFilesParams);
        yield putAndWait(fetchCampaignFiles(campaignId, params, { source: DETAILS_SOURCE }));
      }
    }
    yield put(actions.isInitialized());
  }
}

function* goBackOnDashboard() {
  yield call(navigate, generatePath(PATH.CAMPAIGNS_DASHBOARD.path));
}

let isSendingRunning = false;
function* refreshSendingDetailsWorker({ payload }) {
  if (!isSendingRunning) {
    isSendingRunning = true;
    let currentStatus = payload.status;
    while (currentStatus === CAMPAIGN_STATUS_ENUM.SENDING) {
      yield call(refreshDetailsPage);
      const { status } = yield select(getSelectedCampaign);
      currentStatus = status;
      yield delay(10000);
    }
    isSendingRunning = false;
  }
}

function* refreshDetailsPage() {
  const campaignId = yield select(getSelectedCampaignId);
  const params = yield select(getCampaignFilesParams);
  const isTriggered = yield select(isSelectedCampaignTriggered);
  if (isTriggered) {
    yield putAndWait(fetchCampaignFiles(campaignId, params, { source: DETAILS_SOURCE }));
  }
  yield putAndWait(core.fetchCampaign(campaignId, { source: DETAILS_SOURCE }));
}

function* onDetailsWorker() {
  yield takeEvery([types.FETCH_CAMPAIGN_SUCCESS, types.SEND_CAMPAIGN_SUCCESS], refreshSendingDetailsWorker);
  yield takeLatest(
    [
      WS_CAMPAIGN_BULK_INTERVENTIONS_CREATED_COMPLETED,
      WS_CAMPAIGN_BULK_FOLLOW_COMPLETED,
      types.UNFOLLOW_CAMPAIGN_SUCCESS,
      types.FOLLOW_CAMPAIGN_SUCCESS,
    ],
    refreshDetailsPage
  );
  yield call(initDetailsPage);
  yield takeEvery(shouldGoBackToDashboard, goBackOnDashboard);
  yield takeEvery(duplicateCampaignMatcher, afterCreateCampaign);
}

export default function* campaignsDashboardSaga() {
  yield scopeToPage([PATH.CAMPAIGNS_DETAILS], onDetailsWorker);
  yield fork(campaignSocket);
}
